import ProductImage from '../ProductImage/ProductImage';
import productTableStyles from '../ProductTable/ProductTable.module.css';

/**
 * Card flutuante de hover do produto (REPOSIÇÕES + HISTÓRICO DE VENDAS + médias +
 * Est. Mín + duração). Fonte ÚNICA: usado no catálogo (grid) e no modal de Compras
 * Automáticas, pra os dois mostrarem exatamente o mesmo card.
 *
 * @param {object} produto  — produto vindo de fetchEcommerceProdutos (tem historicoEntradas,
 *                            vendasMensais, mediaMensal, mediaDiariaAjustada, estoqueMinimo...).
 * @param {object} [style]  — posição/zindex (a classe .tooltip já é position:fixed).
 */
export default function ProductHoverCard({ produto, style, className }) {
  if (!produto) return null;
  const getImageSrc = (p) => p.imagemURL;

  return (
    <div
      className={`${productTableStyles.tooltip} ${className || ''}`}
      style={{ display: 'block', ...style }}
    >
      <div className={productTableStyles.tooltipImageContainer}>
        <ProductImage
          src={getImageSrc(produto)}
          alt={produto.nome}
          className={productTableStyles.tooltipMainImage}
        />
      </div>

      <div className={productTableStyles.tooltipHeader}>
        {produto.nome}
      </div>

      <div className={productTableStyles.tooltipContent}>
        {/* Seção de Reposições Individuais */}
        {(() => {
          const entradas = produto.historicoEntradas;
          const MAX_VISIBLE = 2;
          const MESES_ABREV = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          // precisao 'mes' = registro do relatório mensal (dia é fictício) → mostra só Mês/Ano
          const fmtData = (s, precisao) => {
            if (!s) return '';
            const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!m) return s;
            if (precisao === 'mes') return `${MESES_ABREV[parseInt(m[2], 10) - 1]}/${m[1]}`;
            return `${m[3]}/${m[2]}/${m[1]}`;
          };

          if (entradas && entradas.length > 0) {
            const visiveis = entradas.slice(0, MAX_VISIBLE);
            const extras = entradas.length - MAX_VISIBLE;
            return (
              <div style={{ marginBottom: '8px' }}>
                <div style={{ fontSize: '10px', fontWeight: 700, color: '#1e8449', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                  📦 Reposicoes
                </div>
                {visiveis.map((entrada, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 8px', marginBottom: '2px', background: i % 2 === 0 ? 'rgba(46, 204, 113, 0.08)' : 'transparent', borderRadius: '4px', fontSize: '10.5px' }}>
                    <span style={{ color: '#555', minWidth: '72px', fontVariantNumeric: 'tabular-nums' }}>
                      {fmtData(entrada.data, entrada.precisao)}
                    </span>
                    <span style={{ background: 'linear-gradient(135deg, #27ae60, #1e8449)', color: '#fff', padding: '1px 6px', borderRadius: '8px', fontWeight: 700, fontSize: '10px' }}>
                      +{entrada.quantidade} un
                    </span>
                    <span style={{ color: '#888', fontSize: '9.5px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entrada.origem || ''}
                    </span>
                  </div>
                ))}
                {extras > 0 && (
                  <div style={{ fontSize: '10px', color: '#7f8c8d', paddingLeft: '8px', marginTop: '2px' }}>
                    +{extras} anteriores
                  </div>
                )}
              </div>
            );
          }
          if (produto.qtdEntrada12m > 0 && produto.ultimaEntrada) {
            return (
              <div style={{ marginBottom: '8px', padding: '6px 10px', background: 'rgba(46, 204, 113, 0.15)', borderRadius: '6px', border: '1px solid rgba(46, 204, 113, 0.3)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '11px' }}>
                <span style={{ color: '#2ecc71', fontWeight: 600 }}>📦 Entrada:</span>
                <span style={{ color: '#1e8449', fontWeight: 700 }}>
                  {(() => {
                    const m = String(produto.ultimaEntrada).match(/^(\d{4})-(\d{2})-(\d{2})/);
                    return m ? `${m[3]}/${m[2]}/${m[1]}` : new Date(produto.ultimaEntrada).toLocaleDateString('pt-BR');
                  })()}
                </span>
                <span style={{ color: '#2ecc71' }}>
                  (+{produto.qtdEntrada12m} un.)
                </span>
              </div>
            );
          }
          return null;
        })()}

        {produto.vendasMensais && produto.vendasMensais.length > 0 ? (
          <>
            <div className={productTableStyles.tooltipSubHeader}>
              Histórico de Vendas
            </div>
            {produto.vendasMensais.map((venda, index) => {
              const nextVenda = produto.vendasMensais[index + 1];
              let percentChange = null;

              if (nextVenda) {
                if (nextVenda.quantidade > 0) {
                  percentChange = ((venda.quantidade - nextVenda.quantidade) / nextVenda.quantidade) * 100;
                } else if (venda.quantidade > 0) {
                  percentChange = 100;
                } else {
                  percentChange = 0;
                }
              }

              return (
                <div key={index} className={`${productTableStyles.tooltipRow} ${index % 2 !== 0 ? productTableStyles.rowStriped : ''}`}>
                  <span className={productTableStyles.tooltipLabel}>
                    {venda.mes}
                    {venda.qtdEntrada > 0 && (
                      <span
                        style={{ marginLeft: '6px', background: 'linear-gradient(135deg, #27ae60, #1e8449)', color: 'white', padding: '1px 6px', borderRadius: '8px', fontSize: '9px', fontWeight: 600 }}
                        title={venda.dataEntrada ? `Entrada: ${new Date(venda.dataEntrada).toLocaleDateString('pt-BR')}` : 'Entrada registrada'}
                      >
                        📦+{venda.qtdEntrada}
                      </span>
                    )}
                  </span>
                  <div className={productTableStyles.tooltipRightSide}>
                    {percentChange !== null && Math.abs(percentChange) > 0 && (
                      <span className={`${productTableStyles.percentChange} ${percentChange >= 0 ? productTableStyles.percentPositive : productTableStyles.percentNegative}`}>
                        {percentChange > 0 ? '▲' : '▼'} {Math.abs(percentChange).toFixed(0)}%
                      </span>
                    )}
                    <span className={productTableStyles.tooltipValue}>{venda.quantidade}</span>
                  </div>
                </div>
              );
            })}
            <div className={productTableStyles.tooltipDividerStrong}></div>
            <div className={productTableStyles.tooltipRow}>
              <span className={productTableStyles.tooltipLabel}>Média Mensal</span>
              <span className={productTableStyles.tooltipValue}>{produto.mediaMensal || 0}</span>
            </div>
            {produto.estoque > 0 && (
              <div className={productTableStyles.tooltipRow}>
                <span className={productTableStyles.tooltipLabel}>Média Diária</span>
                <span className={productTableStyles.tooltipValue}>
                  {produto.mediaDiariaAjustada > 0
                    ? produto.mediaDiariaAjustada.toFixed(1)
                    : '—'}
                </span>
              </div>
            )}
            <div className={productTableStyles.tooltipDivider}></div>
            <div className={productTableStyles.tooltipInfo}>
              <strong>Est. Mín: {produto.estoqueMinimo}</strong><br />
              <small>(Média Mensal × 1,5)</small>
            </div>
            {produto.estoque > 0 && (
              <div className={productTableStyles.tooltipInfoSecondary}>
                <small>
                  Duração estimada: <strong>
                    {produto.mediaMensal > 0
                      ? Math.floor(produto.estoque / (produto.mediaMensal / 30))
                      : '∞'
                    } dias
                  </strong>
                </small>
              </div>
            )}
            {produto.dataCadastro && (
              <div className={productTableStyles.tooltipInfoSecondary} style={{ marginTop: '8px', borderTop: '1px dashed #555', paddingTop: '8px' }}>
                <small>
                  📅 Cadastrado em: <strong>
                    {new Date(produto.dataCadastro).toLocaleDateString('pt-BR')}
                  </strong>
                </small>
              </div>
            )}
          </>
        ) : (
          <div className={productTableStyles.tooltipInfo}>
            Sem dados de vendas disponíveis
          </div>
        )}
      </div>
    </div>
  );
}
